import type { TConstraints } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type { IBannersnackAttributes, IBannersnackClass } from './bannersnack';
import type { ICreativeAttributes } from './creatives_base';
import { createDimensionValidator, CreativeClass, ValidDimensions } from './creatives_base';
import type { ICreativeAuditStatus } from './creatives_display';
import { DisplayCreative } from './creatives_display';
import type { IAdTagBaseCreative } from './creatives_display_adtag';

// =============================================================================
// Bannersnack Creative Types
// =============================================================================

export interface IBannersnackCreative extends IAdTagBaseCreative, IBannersnackAttributes {
  readonly _cls: CreativeClass.DisplayBannersnack;
  audit_statuses: ICreativeAuditStatus[];
}

interface IBannersnackCreativeEditorUrlResponse extends Record<string, unknown> {
  url: string;
}

/**
 * Type guard that narrows a DisplayCreative to BannersnackCreative.
 *
 * @param creative - The creative to check
 * @returns True if the creative is a BannersnackCreative instance
 */
export function isBannersnackCreative(creative: DisplayCreative): creative is BannersnackCreative {
  return creative.get('_cls') === CreativeClass.DisplayBannersnack;
}

/**
 * Type guard that narrows plain creative attributes to IBannersnackCreative.
 * Useful for checking toJS() output or other plain objects.
 *
 * @param attrs - The creative attributes to check
 * @returns True if the attributes belong to a Bannersnack creative
 */
export function isBannersnackCreativeAttributes(
  attrs: ICreativeAttributes,
): attrs is IBannersnackCreative {
  return attrs._cls === CreativeClass.DisplayBannersnack;
}

// =============================================================================
// Bannersnack Creative Class
// =============================================================================

export class BannersnackCreative
  extends DisplayCreative<IBannersnackCreative>
  implements IBannersnackClass
{
  public override readonly className = 'BannersnackCreative';

  public override get creativeType(): string {
    return this.get('mimetype')?.includes('video') ? 'Bannersnack Video' : 'Bannersnack Image';
  }

  public override get constraints(): TConstraints<IBannersnackCreative> {
    return {
      ...super.constraints,
      _cls: {
        inclusion: [CreativeClass.DisplayBannersnack],
      },
      url: undefined,
      width: {
        ...super.constraints.width,
        equality: createDimensionValidator(ValidDimensions, 'width'),
      },
      height: {
        ...super.constraints.height,
        equality: createDimensionValidator(ValidDimensions, 'height'),
      },
    };
  }

  public override getDefaults(): Partial<IBannersnackCreative> {
    return {
      ...super.getDefaults(),
      _cls: CreativeClass.DisplayBannersnack,
    };
  }

  public async editorURL(): Promise<string> {
    this.assertCollection(this.collection);

    const response = await wretch<IBannersnackCreativeEditorUrlResponse>(
      this.collection.url() + this.id + '/editor',
      {
        method: 'GET',
        headers: this.collection.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data.url;
  }

  public async sync(timestamp: number): Promise<void> {
    this.assertCollection(this.collection);

    const RETRY_DELAY = 1000;
    const MAX_RETRIES = 5;
    const TIMEOUT = 30000;

    let retryCount = 0;
    const startTime = Date.now();

    while (this.isUpdating) {
      if (retryCount >= MAX_RETRIES) {
        throw new Error('Max retries reached while waiting for sync to complete');
      }
      if (Date.now() - startTime > TIMEOUT) {
        throw new Error('Timeout while waiting for sync to complete');
      }

      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      retryCount++;
    }

    this.isUpdating = true;

    try {
      const response = await wretch<Partial<IBannersnackCreative>>(
        this.collection.url() + this.id + '/sync?ts=' + timestamp,
        {
          method: 'GET',
          headers: this.collection.getHeaders(),
        },
      );

      if (isWretchError(response)) {
        throw response.error;
      }

      if (!this.get('banner_hash')) {
        this.set({ banner_hash: response.data.banner_hash });
      }

      if (response.data.width && response.data.height) {
        const dimensionKey = response.data.width + 'x' + response.data.height;
        response.data.spec = ValidDimensions.hasOwnProperty(dimensionKey)
          ? ValidDimensions[dimensionKey].spec
          : dimensionKey;
      }

      await this.patch({ spec: response.data.spec });

      this.set({
        adtag: response.data.adtag,
        animated: response.data.animated,
        banner_urls: response.data.banner_urls,
        height: response.data.height,
        name: response.data.name,
        width: response.data.width,
        spec: response.data.spec,
      });
    } catch (error) {
      throw new Error('Failed to sync creative: ' + error);
    } finally {
      this.isUpdating = false;
    }
  }
}
